/**
 * The current tenant's branding (#2387): its name and optional logo.
 *
 * App-level rather than feature-level because two places read it: the
 * sidebar of every signed-in member (`components/layout/SidebarBrand`) and
 * the branding card of the admin settings page (`features/tenants`).
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@lib/queryKeys';
import { tenantBrandingAPI } from '@/services/tenants';

export function useTenantBranding() {
  return useQuery({
    queryKey: queryKeys.tenantBranding.current(),
    queryFn: () => tenantBrandingAPI.get(),
  });
}

/**
 * An object URL for the logo behind `logoUrl`, or null while there is none,
 * it is loading, or it failed. The URL is revoked when the logo changes or
 * the caller unmounts.
 */
export function useTenantLogoSrc(logoUrl: string | null): string | null {
  const logo = useQuery({
    queryKey: queryKeys.tenantBranding.logo(logoUrl ?? ''),
    queryFn: () => tenantBrandingAPI.fetchLogo(logoUrl ?? ''),
    enabled: logoUrl !== null,
    // logoUrl is versioned by the logo's etag: the same URL is the same bytes.
    staleTime: Infinity,
  });
  const blob = logoUrl === null ? undefined : logo.data;
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (blob === undefined) {
      setSrc(null);
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);
  return src;
}
